import { useState, useMemo, useEffect, lazy, Suspense } from 'react'
import { CampaignSummary, startCampaign, stopCampaign, deleteCampaign, copyCampaign, toggleStarCampaign, updateCampaign, HourlyPoint, HourlyCampaignPoint } from '../api'

const ConfigManagerModal = lazy(() => import('./ConfigManagerModal'))
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Card } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Play, Square, Plus, Loader2, Archive,
  Copy, Trash2, Search, Clock, CheckCircle2, XCircle, ShieldAlert, Star, Gauge, FolderArchive, FolderOpen, Pencil, ExternalLink,
} from 'lucide-react'

interface Props {
  projects: CampaignSummary[]
  onSelectProject: (id: string) => void
  onNewProject: () => void
  onRefresh: () => void
  onAlert: (msg: { type: 'success' | 'error'; text: string }) => void
  canFull: boolean
  canEdit: boolean
  hourlyHistory?: HourlyPoint[]
  hourlyCampaignHistory?: HourlyCampaignPoint[]
}

function formatDate(ts: number): string {
  if (!ts) return '—'
  const d = new Date(ts * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
}

function formatUptime(startedAt: number): string {
  const sec = Math.floor(Date.now() / 1000 - startedAt)
  if (sec < 60) return `${sec}с`
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  if (h > 0) return `${h}ч ${m}м`
  return `${m}м`
}

function timeUntil(schedTime: string): string {
  const [h, m] = schedTime.split(':').map(Number)
  if (isNaN(h) || isNaN(m)) return schedTime
  const now = new Date()
  const nowMin = now.getHours() * 60 + now.getMinutes()
  const targetMin = h * 60 + m
  let diff = targetMin - nowMin
  if (diff <= 0) diff += 24 * 60
  const dh = Math.floor(diff / 60)
  const dm = diff % 60
  if (dh > 0) return `через ${dh}ч ${dm}м`
  return `через ${dm}м`
}

// Bug #134: для continuous/interval/hourly start_time не используется
// (см. server.py _scheduler_tick), поэтому показывать "через X часов"
// бессмысленно — оператор видит мифический «запуск в 5 утра» для
// проекта, который на самом деле работает непрерывно. Возвращаем
// человекочитаемый ярлык по типу расписания.
function schedBadgeLabel(p: any): string {
  const t = p.sched_type || 'time'
  if (t === 'continuous') return 'Непрерывно'
  if (t === 'interval') return 'С интервалом'
  if (t === 'hourly') return 'Каждый час'
  if ((t === 'time' || t === 'daily') && p.sched_start_time) return timeUntil(p.sched_start_time)
  return 'По расписанию'
}

const DAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

function WeekBarChart({ data }: { data: { day: string; ok: number }[] }) {
  const max = Math.max(1, ...data.map(d => d.ok))
  return (
    <div className="flex items-end justify-center gap-1 md:justify-start md:gap-[5px]">
      {data.map((d, i) => {
        const pct = d.ok > 0 ? Math.max(12, (d.ok / max) * 100) : 8
        return (
          <div key={i} className="group/bar relative flex flex-col items-center gap-0.5 md:gap-1">
            {d.ok > 0 && (
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 opacity-0 group-hover/bar:opacity-100 transition-opacity pointer-events-none z-10 md:-top-6">
                <div className="rounded px-1 py-0.5 text-[9px] font-medium tabular-nums bg-popover border border-border text-popover-foreground shadow-md whitespace-nowrap md:px-1.5 md:text-[10px]">
                  {d.ok}
                </div>
              </div>
            )}
            <div className="flex h-7 w-[7px] flex-col justify-end md:h-9 md:w-[10px]">
              <div
                className={`w-full rounded-sm transition-colors ${d.ok > 0 ? 'bg-primary/60 group-hover/bar:bg-primary' : 'bg-muted/50'}`}
                style={{ height: `${pct}%`, minHeight: d.ok > 0 ? 3 : 2 }}
              />
            </div>
            <span className="text-[7px] text-muted-foreground leading-none md:text-[8px]">{d.day}</span>
          </div>
        )
      })}
    </div>
  )
}

function getLast7Days(): string[] {
  const days: string[] = []
  const now = new Date()
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now)
    d.setDate(d.getDate() - i)
    days.push(d.toISOString().split('T')[0])
  }
  return days
}

type SortMode = 'default' | 'name_asc' | 'name_desc' | 'created_new' | 'created_old'

const SORT_LABEL: Record<SortMode, string> = {
  default: 'По активности',
  name_asc: 'По названию А→Я',
  name_desc: 'По названию Я→А',
  created_new: 'Сначала новые',
  created_old: 'Сначала старые',
}

export default function ProjectsPage({ projects, onSelectProject, onNewProject, onRefresh, onAlert, canFull, canEdit, hourlyHistory = [], hourlyCampaignHistory = [] }: Props) {
  const [filter, setFilter] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<CampaignSummary | null>(null)
  const [showConfigManager, setShowConfigManager] = useState(false)
  // Idea #47: запоминаем выбор пользователя в localStorage
  const [sortMode, setSortMode] = useState<SortMode>(() => {
    const saved = (typeof window !== 'undefined' ? localStorage.getItem('projectsSortMode') : null) || 'default'
    return (Object.keys(SORT_LABEL).includes(saved) ? saved : 'default') as SortMode
  })
  useEffect(() => {
    try { localStorage.setItem('projectsSortMode', sortMode) } catch { /* private mode */ }
  }, [sortMode])

  const active = useMemo(() => projects.filter(p => !p.archived), [projects])
  const archived = useMemo(() => projects.filter(p => p.archived), [projects])
  const displayed = showArchived ? archived : active
  const filtered = useMemo(() => {
    let list = displayed
    if (filter.trim()) {
      const q = filter.toLowerCase()
      list = list.filter(p => p.name.toLowerCase().includes(q) || (p.comment || '').toLowerCase().includes(q))
    }
    const collator = new Intl.Collator('ru', { sensitivity: 'base', numeric: true })
    // Группировка Активные/Остановленные сохраняется ВСЕГДА — пользовательская
    // сортировка применяется ВНУТРИ каждой группы. Так юзер не теряет
    // привычный UX и может одновременно видеть алфавитный порядок.
    return [...list].sort((a, b) => {
      if (a.running !== b.running) return a.running ? -1 : 1
      if (sortMode === 'name_asc') return collator.compare(a.name || '', b.name || '')
      if (sortMode === 'name_desc') return collator.compare(b.name || '', a.name || '')
      if (sortMode === 'created_new') return ((b as any).created_at || 0) - ((a as any).created_at || 0)
      if (sortMode === 'created_old') return ((a as any).created_at || 0) - ((b as any).created_at || 0)
      // default — внутри группы по starred
      if (a.starred !== b.starred) return (b.starred ? 1 : 0) - (a.starred ? 1 : 0)
      return collator.compare(a.name || '', b.name || '')
    })
  }, [displayed, filter, sortMode])

  const filteredRunning = useMemo(() => filtered.filter(p => p.running), [filtered])
  const filteredStopped = useMemo(() => filtered.filter(p => !p.running), [filtered])

  const runningCount = active.filter(p => p.running).length
  const stoppedCount = active.length - runningCount

  const last7 = useMemo(() => getLast7Days(), [])

  const campaignWeekData = useMemo(() => {
    const dayMap = new Map<string, Map<string, { ok: number; fail: number; captcha: number }>>()
    const hourMap = new Map<string, number[]>()
    for (const h of hourlyCampaignHistory) {
      const day = h.hour.split('T')[0]
      if (!dayMap.has(h.campaign_id)) dayMap.set(h.campaign_id, new Map())
      const days = dayMap.get(h.campaign_id)!
      const b = days.get(day) || { ok: 0, fail: 0, captcha: 0 }
      b.ok += h.ok; b.fail += h.fail; b.captcha += h.captcha
      days.set(day, b)
      if (h.ok > 0) {
        if (!hourMap.has(h.campaign_id)) hourMap.set(h.campaign_id, [])
        hourMap.get(h.campaign_id)!.push(h.ok)
      }
    }
    const result = new Map<string, { days: { day: string; ok: number }[]; ok: number; fail: number; captcha: number; okPerHour: number }>()
    for (const [cid, dm] of dayMap) {
      let totalOk = 0, totalFail = 0, totalCaptcha = 0
      const days = last7.map(dateStr => {
        const b = dm.get(dateStr) || { ok: 0, fail: 0, captcha: 0 }
        totalOk += b.ok; totalFail += b.fail; totalCaptcha += b.captcha
        const d = new Date(dateStr)
        return { day: DAY_LABELS[d.getDay() === 0 ? 6 : d.getDay() - 1], ok: b.ok }
      })
      const hourOks = hourMap.get(cid) || []
      const okPerHour = hourOks.length > 0 ? Math.round(hourOks.reduce((a, b) => a + b, 0) / hourOks.length) : 0
      result.set(cid, { days, ok: totalOk, fail: totalFail, captcha: totalCaptcha, okPerHour })
    }
    return result
  }, [hourlyCampaignHistory, last7])

  const handleStart = async (p: CampaignSummary) => {
    setLoadingId(p.id)
    await startCampaign(p.id)
    onAlert({ type: 'success', text: `${p.name} — запущен` })
    setTimeout(() => { onRefresh(); setLoadingId(null) }, 1000)
  }

  const handleStop = async (p: CampaignSummary) => {
    setLoadingId(p.id)
    await stopCampaign(p.id)
    onAlert({ type: 'success', text: `${p.name} — остановлен` })
    setTimeout(() => { onRefresh(); setLoadingId(null) }, 1000)
  }

  const handleCopy = async (p: CampaignSummary) => {
    setLoadingId(p.id)
    try {
      const res = await copyCampaign(p.id)
      if (res.ok) {
        onAlert({ type: 'success', text: `Копия создана: ${res.name}` })
      } else {
        onAlert({ type: 'error', text: 'Не удалось создать копию' })
      }
    } catch {
      onAlert({ type: 'error', text: 'Ошибка при копировании' })
    }
    onRefresh()
    setLoadingId(null)
  }

  const handleDelete = async () => {
    if (!deleteConfirm) return
    await deleteCampaign(deleteConfirm.id)
    setDeleteConfirm(null)
    onRefresh()
  }

  const renderProjectCard = (p: CampaignSummary) => {
    const isLoading = loadingId === p.id
    const weekData = campaignWeekData.get(p.id)

    const useRunStats = !!p.running
    const hasLastRun = !useRunStats && (p.last_run_ok !== undefined || p.last_run_fail !== undefined)
    const weekOk = useRunStats ? (p.run_ok ?? 0) : hasLastRun ? (p.last_run_ok ?? 0) : (weekData?.ok ?? 0)
    const weekFail = useRunStats ? (p.run_fail ?? 0) : hasLastRun ? (p.last_run_fail ?? 0) : (weekData?.fail ?? 0)
    const weekCaptcha = useRunStats ? (p.run_captcha ?? 0) : hasLastRun ? (p.last_run_captcha ?? 0) : (weekData?.captcha ?? 0)
    const weekTotal = weekOk + weekFail
    const successRate = weekTotal > 0 ? Math.round((weekOk / weekTotal) * 100) : 0
    let okPerHour = weekData?.okPerHour ?? 0
    if (useRunStats && p.started_at) {
      const elapsedH = (Date.now() / 1000 - p.started_at) / 3600
      okPerHour = elapsedH > 0.05 ? Math.round((p.run_ok ?? 0) / elapsedH) : 0
    }

    return (
      <Card key={p.id} onClick={() => !p.archived && onSelectProject(p.id)}
        className={`rounded-xl p-0 overflow-hidden transition-all cursor-pointer touch-manipulation select-none ${
          p.running ? 'border-success/25 hover:border-success/50 hover:shadow-lg hover:shadow-success/5'
            : p.archived ? 'opacity-60 cursor-default' : 'hover:border-primary/30 hover:shadow-md'
        }`}>
        {/* Header */}
        <div className="p-4 md:p-5 pb-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5 min-w-0 flex-1">
              {/* Accent stripe */}
              <div className="w-1 h-5 rounded-full shrink-0 mt-0.5"
                style={{ background: p.color || (p.running ? '#22c55e' : 'var(--color-muted)') }} />
              <button
                className="shrink-0 cursor-pointer mt-0.5 -ml-0.5"
                onClick={e => { e.stopPropagation(); toggleStarCampaign(p.id).then(() => onRefresh()) }}
                title={p.starred ? 'Убрать из избранного' : 'В избранное'}
              >
                <Star className={`w-4 h-4 transition-colors ${p.starred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/40 hover:text-yellow-400'}`} />
              </button>
              {/* Idea #107: открыть проект в НОВОЙ вкладке браузера. Deep-link
                  /project/{cid} уже обрабатывается роутером (App.tsx). Обычная
                  <a target=_blank> → работает ctrl/⌘+клик и «открыть в новом
                  окне»; stopPropagation, чтобы клик по иконке не открывал
                  проект в текущем окне через onSelectProject карточки. */}
              {!p.archived && (
                <a
                  href={`/project/${p.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  className="shrink-0 cursor-pointer mt-0.5"
                  title="Открыть в новой вкладке"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/40 hover:text-primary transition-colors" />
                </a>
              )}
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-semibold text-foreground truncate leading-tight">{p.name}</h3>
                {p.comment && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{p.comment}</p>}
              </div>
            </div>
            <div className="shrink-0">
              {p.running ? (
                // Bug #117: ранее тут был janky badge «Нет потоков» при alive=0,
                // но он давал ложные срабатывания для коротких итераций
                // (например Galaxy unique exhausted, итерация 5 сек). Теперь
                // только зелёный «Запущен», а индикатор живых потоков X/Y
                // ниже подсветится янтарным когда alive < total.
                <div className="inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-500 text-[11px]">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                  </span>
                  <span className="font-medium">Запущен</span>
                </div>
              ) : p.sched_enabled ? (
                <Badge variant="outline" className="text-blue-400 border-blue-500/30 h-6">
                  <Clock className="w-3 h-3 mr-1" />{schedBadgeLabel(p)}
                </Badge>
              ) : p.archived ? (
                <Badge variant="secondary" className="h-6">Архив</Badge>
              ) : (
                <div className="inline-flex items-center gap-1.5 h-6 pl-2 pr-2.5 rounded-full border border-border bg-muted/40 text-muted-foreground text-[11px]">
                  <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60" />
                  <span>Остановлен</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Metrics row */}
        <div className="px-4 md:px-5 pb-3">
          {/* ───── Desktop layout (sm+): вертикальные столбики с крупными цифрами ───── */}
          <div className="hidden sm:flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline flex-wrap gap-x-5 gap-y-2">
                <div className="min-w-0">
                  {p.running && p.started_at ? (
                    <>
                      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">Время работы</div>
                      <div className="text-base font-semibold tabular-nums text-foreground leading-tight">{formatUptime(p.started_at)}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">Последний запуск</div>
                      <div className="text-[13px] font-medium tabular-nums text-foreground leading-tight pt-0.5">{formatDate(p.last_run_at ?? p.created_at)}</div>
                    </>
                  )}
                </div>
                {p.running && p.workers_total !== undefined && p.workers_total > 0 && (
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">Потоки</div>
                    <div className="text-base font-semibold tabular-nums leading-tight">
                      <span className={p.workers_active !== undefined && p.workers_active < (p.workers_total ?? 0) ? 'text-amber-500' : 'text-foreground'}>
                        {p.workers_active ?? p.workers_alive ?? 0}
                      </span>
                      <span className="text-muted-foreground"> / {p.workers_total}</span>
                    </div>
                  </div>
                )}
                {weekTotal > 0 && (
                  <>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
                        <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" /> Успешно
                      </div>
                      <div className="text-base font-semibold tabular-nums text-emerald-500 leading-tight">{weekOk.toLocaleString('ru')}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
                        <XCircle className="w-2.5 h-2.5 text-destructive/70" /> Ошибки
                      </div>
                      <div className="text-base font-semibold tabular-nums text-destructive leading-tight">{weekFail.toLocaleString('ru')}</div>
                    </div>
                    {weekCaptcha > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
                          <ShieldAlert className="w-2.5 h-2.5 text-violet-400" /> Капча
                        </div>
                        <div className="text-base font-semibold tabular-nums text-violet-400 leading-tight">{weekCaptcha.toLocaleString('ru')}</div>
                      </div>
                    )}
                    {okPerHour > 0 && (
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium flex items-center gap-1">
                          <Gauge className="w-2.5 h-2.5 text-muted-foreground" /> Скорость
                        </div>
                        <div className="text-base font-semibold tabular-nums text-foreground leading-tight">±{okPerHour.toLocaleString('ru')}<span className="text-[11px] text-muted-foreground font-normal">/ч</span></div>
                        <div className="text-[10px] text-muted-foreground tabular-nums leading-tight">≈{(okPerHour * 24).toLocaleString('ru')}/сут</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            {weekData && weekTotal > 0 && (
              <div className="shrink-0 pointer-events-none">
                <WeekBarChart data={weekData.days} />
              </div>
            )}
          </div>

          {/* ───── Mobile layout (<sm): meta + график сверху, метрики снизу ───── */}
          <div className="sm:hidden">
            {/* Top row: meta слева, график справа */}
            <div className="flex items-start justify-between gap-3 py-1">
              <div className="min-w-0 flex-1 text-[10px] text-muted-foreground leading-tight">
                {p.running && p.started_at
                  ? <>В работе <span className="text-foreground font-medium tabular-nums">{formatUptime(p.started_at)}</span>{p.workers_total !== undefined && p.workers_total > 0 && (<> · <span className="text-foreground font-medium tabular-nums">{p.workers_active ?? p.workers_alive ?? 0}/{p.workers_total}</span> потоков</>)}</>
                  : <>Последний запуск <span className="text-foreground font-medium tabular-nums">{formatDate(p.last_run_at ?? p.created_at)}</span></>}
              </div>
              {weekData && weekTotal > 0 && (
                <div className="shrink-0 pointer-events-none -mt-1">
                  <WeekBarChart data={weekData.days} />
                </div>
              )}
            </div>
            {/* Bottom row: inline метрики через разделитель-точку */}
            {weekTotal > 0 && (
              <div className="flex items-center flex-wrap py-1.5 mt-1 border-t border-border/40">
                <div className="flex items-center gap-1" title="Успешно">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                  <span className="text-[13px] tabular-nums font-semibold text-emerald-500">{weekOk.toLocaleString('ru')}</span>
                </div>
                <span className="mx-2 text-muted-foreground/40">·</span>
                <div className="flex items-center gap-1" title="Ошибки">
                  <XCircle className="w-3 h-3 text-destructive shrink-0" />
                  <span className="text-[13px] tabular-nums font-semibold text-destructive">{weekFail.toLocaleString('ru')}</span>
                </div>
                {weekCaptcha > 0 && (
                  <>
                    <span className="mx-2 text-muted-foreground/40">·</span>
                    <div className="flex items-center gap-1" title="Капча">
                      <ShieldAlert className="w-3 h-3 text-violet-400 shrink-0" />
                      <span className="text-[13px] tabular-nums font-semibold text-violet-400">{weekCaptcha.toLocaleString('ru')}</span>
                    </div>
                  </>
                )}
                {okPerHour > 0 && (
                  <>
                    <span className="mx-2 text-muted-foreground/40">·</span>
                    <div className="flex items-center gap-1" title="Скорость">
                      <Gauge className="w-3 h-3 text-muted-foreground shrink-0" />
                      <span className="text-[13px] tabular-nums font-semibold text-foreground">±{okPerHour.toLocaleString('ru')}</span>
                      <span className="text-[10px] text-muted-foreground">/ч</span>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <Separator />
            <div className="flex flex-wrap items-center gap-0.5 md:gap-1 px-4 md:px-5 py-2 bg-muted/20" onClick={e => e.stopPropagation()}>
              {!p.archived && (
                isLoading ? (
                  <Button variant="ghost" size="sm" disabled className="h-8 gap-1.5 px-2 text-xs md:h-7" title="Загрузка">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>…</span>
                  </Button>
                ) : p.running ? (
                  <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs md:h-7 text-destructive hover:bg-destructive/10" title="Остановить" onClick={() => handleStop(p)}>
                    <Square className="w-3 h-3 shrink-0" />
                    <span>Остановить</span>
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs md:h-7 text-primary hover:bg-primary/10" title="Запустить" onClick={() => handleStart(p)}>
                    <Play className="w-3 h-3 shrink-0" />
                    <span>Запустить</span>
                  </Button>
                )
              )}
              {!p.archived && (
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground md:h-7 md:w-auto md:gap-1.5 md:px-2 md:text-xs" title="Редактировать" onClick={() => onSelectProject(p.id)}>
                  <Pencil className="w-3 h-3 shrink-0" />
                  <span className="hidden md:inline">Редактировать</span>
                </Button>
              )}
              {canFull && !p.archived && (
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground md:h-7 md:w-auto md:gap-1.5 md:px-2 md:text-xs" title="Сделать копию" onClick={() => handleCopy(p)}>
                  <Copy className="w-3 h-3 shrink-0" />
                  <span className="hidden md:inline">Сделать копию</span>
                </Button>
              )}
              {p.archived && canFull && (
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-primary hover:bg-primary/10 md:h-7 md:w-auto md:gap-1.5 md:px-2 md:text-xs"
                  title="Восстановить"
                  onClick={async () => { await updateCampaign(p.id, { archived: false }); onRefresh() }}>
                  <Archive className="w-3 h-3 shrink-0" />
                  <span className="hidden md:inline">Восстановить</span>
                </Button>
              )}
              <div className="ml-auto" />
              {canFull && !p.running && !p.archived && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="В архив"
                  onClick={async (e) => { e.stopPropagation(); await updateCampaign(p.id, { archived: true }); onRefresh() }}>
                  <Archive className="w-3.5 h-3.5" />
                </Button>
              )}
              {canFull && !p.running && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => setDeleteConfirm(p)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
      </Card>
    )
  }

  return (
    <div className="p-4 animate-fade-in md:p-6 max-w-5xl">
      <div className="mb-6">
        <div className="min-w-0 mb-3">
          <h1 className="text-xl font-semibold tracking-tight text-foreground flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-primary" />
            Мои проекты
          </h1>
          <p className="text-xs text-muted-foreground mt-1 md:text-sm">
            {active.length} проектов · {runningCount} запущено · {stoppedCount} остановлено
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
          {canFull && (
            <Button size="sm" onClick={onNewProject} className="gap-1.5 text-xs shrink-0" title="Новый проект">
              <Plus className="w-4 h-4 shrink-0" />
              <span>Новый проект</span>
            </Button>
          )}
          {active.length > 0 && (
            <>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs bg-transparent text-muted-foreground hover:text-foreground"
                onClick={async () => { const stopped = active.filter(p => !p.running); for (const p of stopped) await startCampaign(p.id); setTimeout(onRefresh, 1500) }}>
                <Play className="w-3.5 h-3.5" /> Запустить все
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs bg-transparent text-muted-foreground hover:text-foreground"
                onClick={async () => { const running = active.filter(p => p.running); for (const p of running) await stopCampaign(p.id); setTimeout(onRefresh, 1500) }}>
                <Square className="w-3.5 h-3.5" /> Остановить все
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" className="gap-1.5 text-xs bg-transparent text-muted-foreground hover:text-foreground"
            onClick={() => setShowConfigManager(true)}>
            <FolderArchive className="w-3.5 h-3.5" /> Конфигурации
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Поиск проектов..." value={filter} onChange={e => setFilter(e.target.value)} className="pl-9" />
        </div>
        <select
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value as SortMode)}
          className="h-9 rounded-md border border-border bg-background px-2.5 text-xs text-foreground hover:border-foreground/30 transition shrink-0 self-start sm:self-auto cursor-pointer"
          title="Порядок проектов в списке"
        >
          {(Object.keys(SORT_LABEL) as SortMode[]).map(k => (
            <option key={k} value={k}>{SORT_LABEL[k]}</option>
          ))}
        </select>
        <div className="flex items-center gap-1 rounded-lg border border-border p-0.5 bg-muted/50 shrink-0 self-start sm:self-auto">
          <button onClick={() => setShowArchived(false)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${!showArchived ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            Активные ({active.length})
          </button>
          <button onClick={() => setShowArchived(true)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer ${showArchived ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            Архив ({archived.length})
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <p className="text-sm">{filter ? 'Ничего не найдено' : showArchived ? 'Архив пуст' : 'Нет проектов'}</p>
          {!filter && !showArchived && canFull && (
            <Button variant="outline" onClick={onNewProject} className="mt-4 gap-2"><Plus className="w-4 h-4" /> Создать первый проект</Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredRunning.length > 0 && !showArchived && (
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold text-foreground">Активные проекты</h2>
              <Badge variant="secondary" className="h-5 px-2 text-[10px] tabular-nums">{filteredRunning.length}</Badge>
            </div>
          )}
          {filtered.map(p => {
            if (!showArchived && filteredRunning.length > 0 && filteredStopped.length > 0 && p === filteredStopped[0]) {
              return (
                <div key={`__stopped_header_${p.id}`}>
                  <div className="flex items-center gap-3 mt-4 mb-1">
                    <h2 className="text-lg font-semibold text-foreground">Остановленные</h2>
                    <Badge variant="secondary" className="h-5 px-2 text-[10px] tabular-nums">{filteredStopped.length}</Badge>
                  </div>
                  {renderProjectCard(p)}
                </div>
              )
            }
            return renderProjectCard(p)
          })}
        </div>
      )}

      <AlertDialog open={!!deleteConfirm} onOpenChange={o => { if (!o) setDeleteConfirm(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить проект?</AlertDialogTitle>
            <AlertDialogDescription>Проект <strong>{deleteConfirm?.name}</strong> и все его файлы будут удалены.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Удалить</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Suspense fallback={null}>
        {showConfigManager && (
          <ConfigManagerModal
            open={showConfigManager}
            onClose={() => setShowConfigManager(false)}
            onImported={onRefresh}
          />
        )}
      </Suspense>
    </div>
  )
}
